import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Badge, 
  Menu, 
  MenuItem, 
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import SendIcon from '@mui/icons-material/Send';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useAuth } from '../contexts/AuthContext';
import { lottoService } from '../services/LottoService';
import { APP_VERSION } from '../version';

interface HeaderProps {
  title: string;
  alertCount?: number;
}

const Header: React.FC<HeaderProps> = ({ title, alertCount = 0 }) => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [currentSequence, setCurrentSequence] = useState<number | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentSequence();
    }
  }, [isAuthenticated]);
  
  const fetchCurrentSequence = async () => {
    try {
      const userToken = localStorage.getItem('authToken');
      if (!userToken) return;
      
      const result = await lottoService.getThisWeekPrize(userToken);
      if (result && result.result) {
        setCurrentSequence(result.result.sequenceNo);
      }
    } catch (error) {
      console.error('현재 회차 정보 조회 실패:', error);
    }
  };
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleSettings = () => {
    handleMenuClose();
    navigate('/settings');
  };
  
  const openLogoutDialog = () => {
    handleMenuClose();
    setLogoutDialogOpen(true);
  };
  
  const closeLogoutDialog = () => {
    setLogoutDialogOpen(false);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      closeLogoutDialog();
      navigate('/login');
    } catch (error) {
      console.error('로그아웃 실패:', error);
      closeLogoutDialog();
    }
  };
  
  const handleResendNumbers = () => {
    handleMenuClose();
    if (!currentSequence) {
      setSnackbarMessage('현재 회차 정보를 가져올 수 없습니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    // 재조합 여부를 먼저 물어본다 (구매 후 조합이 바뀌면 당첨확인 불일치)
    setResendDialogOpen(true);
  };

  const closeResendDialog = () => {
    setResendDialogOpen(false);
  };

  // keepExisting: true = 이전 조합 그대로, false = 새로 조합
  const handleResendConfirm = async (keepExisting: boolean) => {
    setResendDialogOpen(false);

    if (!currentSequence) {
      setSnackbarMessage('현재 회차 정보를 가져올 수 없습니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsSending(true);

      const userToken = localStorage.getItem('authToken');
      if (!userToken) {
        throw new Error('사용자 토큰이 없습니다.');
      }

      await lottoService.sendChoicedNumbers(userToken, currentSequence, keepExisting);

      setSnackbarMessage(
        keepExisting
          ? '이전 조합 그대로 이메일로 재발송되었습니다.'
          : '새로 조합한 번호가 이메일로 재발송되었습니다.'
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('번호 재발송 실패:', error);
      setSnackbarMessage(error instanceof Error ? error.message : '번호 재발송에 실패했습니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSending(false);
    }
  };
  
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => navigate('/prize')}
            sx={{ mr: 2 }}
            aria-label="홈으로 이동"
          >
            <HomeIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            {title}
            <Typography component="span" variant="caption" sx={{ opacity: 0.7, fontWeight: 400 }}>
              v{APP_VERSION}
            </Typography>
          </Typography>
          
          {isAuthenticated ? (
            <>
              {isSending && (
                <CircularProgress 
                  size={24} 
                  color="inherit" 
                  sx={{ mr: 2 }} 
                />
              )}
              
              <IconButton
                color="inherit"
                onClick={() => navigate('/messages')}
              >
                <Badge badgeContent={alertCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              
              <IconButton
                color="inherit"
                onClick={handleMenuOpen}
                aria-controls="profile-menu"
                aria-haspopup="true"
              >
                <AccountCircleIcon />
              </IconButton>
              
              <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleSettings}>
                  <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                  설정
                </MenuItem>
                <MenuItem onClick={handleResendNumbers}>
                  <SendIcon fontSize="small" sx={{ mr: 1 }} />
                  번호 재발송
                </MenuItem>
                <Divider />
                <MenuItem onClick={openLogoutDialog} sx={{ color: 'error.main' }}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                  로그아웃
                </MenuItem>
              </Menu>
              
              {/* 로그아웃 확인 다이얼로그 */}
              <Dialog
                open={logoutDialogOpen}
                onClose={closeLogoutDialog}
                aria-labelledby="logout-dialog-title"
                aria-describedby="logout-dialog-description"
              >
                <DialogTitle id="logout-dialog-title">
                  로그아웃
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="logout-dialog-description">
                    정말 로그아웃 하시겠습니까?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeLogoutDialog} color="primary">
                    취소
                  </Button>
                  <Button onClick={handleLogout} color="error" autoFocus>
                    로그아웃
                  </Button>
                </DialogActions>
              </Dialog>

              {/* 번호 재발송 방식 선택 다이얼로그 */}
              <Dialog
                open={resendDialogOpen}
                onClose={closeResendDialog}
                aria-labelledby="resend-dialog-title"
                aria-describedby="resend-dialog-description"
              >
                <DialogTitle id="resend-dialog-title">
                  번호 재발송
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="resend-dialog-description">
                    번호를 어떻게 재발송할까요?<br />
                    이미 구매하셨다면 <strong>이전 조합 그대로</strong>를 선택해야 당첨확인이 일치합니다.
                  </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
                  <Button onClick={closeResendDialog} color="inherit">
                    취소
                  </Button>
                  <Button onClick={() => handleResendConfirm(false)} color="secondary">
                    새로 조합
                  </Button>
                  <Button onClick={() => handleResendConfirm(true)} color="primary" variant="contained" autoFocus>
                    이전 조합 그대로
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                color="inherit" 
                onClick={handleSignUp}
                startIcon={<PersonAddIcon />}
              >
                회원가입
              </Button>
              <Button 
                color="inherit" 
                onClick={handleLogin}
                startIcon={<LoginIcon />}
              >
                로그인
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Header; 